import { useState, useEffect, useRef } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { MainWorkOrder } from '@/types';
import { Modal } from '@/components/UI/Modal';
import { toast } from '@/components/UI/Toast';

interface MainWorkOrderFormProps {
  isOpen: boolean;
  onClose: () => void;
  editingOrder?: MainWorkOrder;
}

interface FormErrors {
  orderId?: string;
  workflowId?: string;
  totalQuantity?: string;
  plannedStart?: string;
  plannedEnd?: string;
}

export function MainWorkOrderForm({ isOpen, onClose, editingOrder }: MainWorkOrderFormProps) {
  const orders = useOrderStore((s) => s.orders);
  const addMainWorkOrder = useOrderStore((s) => s.addMainWorkOrder);
  const updateMainWorkOrder = useOrderStore((s) => s.updateMainWorkOrder);
  const updateOrder = useOrderStore((s) => s.updateOrder);
  const workflows = useResourceStore((s) => s.workflows);

  const isEditMode = !!editingOrder;
  const firstInputRef = useRef<HTMLSelectElement>(null);

  const [formData, setFormData] = useState({
    orderId: '',
    workflowId: '',
    totalQuantity: 100,
    completedQuantity: 0,
    plannedStart: '',
    plannedEnd: '',
    note: '',
    status: '待處理' as MainWorkOrder['status'],
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (editingOrder) {
      setFormData({
        orderId: editingOrder.orderId,
        workflowId: editingOrder.workflowId,
        totalQuantity: editingOrder.totalQuantity,
        completedQuantity: editingOrder.completedQuantity,
        plannedStart: editingOrder.plannedStart.slice(0, 10),
        plannedEnd: editingOrder.plannedEnd.slice(0, 10),
        note: editingOrder.note || '',
        status: editingOrder.status,
      });
    } else {
      setFormData({
        orderId: '',
        workflowId: '',
        totalQuantity: 100,
        completedQuantity: 0,
        plannedStart: '',
        plannedEnd: '',
        note: '',
        status: '待處理',
      });
      setErrors({});
      setTouched(new Set());
    }
  }, [editingOrder, isOpen]);

  useEffect(() => {
    if (isOpen) firstInputRef.current?.focus();
  }, [isOpen]);

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'orderId':
        return !value ? '請選擇所屬訂單' : '';
      case 'workflowId':
        return !value ? '請選擇工藝流程' : '';
      case 'totalQuantity':
        if (!value || value <= 0) return '數量必須大於 0';
        return '';
      case 'plannedStart':
        return !value ? '請選擇計畫開始日' : '';
      case 'plannedEnd':
        if (!value) return '請選擇計畫結束日';
        if (formData.plannedStart && new Date(value) <= new Date(formData.plannedStart))
          return '結束日必須晚於開始日';
        return '';
      default:
        return '';
    }
  };

  const handleChange = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (touched.has(name)) {
      const error = validateField(name, value);
      setErrors((prev) => {
        const next = { ...prev };
        if (error) (next as any)[name] = error;
        else delete (next as any)[name];
        return next;
      });
    }
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => new Set([...prev, name]));
    const error = validateField(name, (formData as any)[name]);
    setErrors((prev) => {
      const next = { ...prev };
      if (error) (next as any)[name] = error;
      else delete (next as any)[name];
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};
    for (const key of ['orderId', 'workflowId', 'totalQuantity', 'plannedStart', 'plannedEnd'] as const) {
      const error = validateField(key, (formData as any)[key]);
      if (error) (newErrors as any)[key] = error;
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(Object.keys(newErrors)));
      return;
    }

    if (isEditMode && editingOrder) {
      updateMainWorkOrder(editingOrder.id, {
        orderId: formData.orderId,
        workflowId: formData.workflowId,
        totalQuantity: formData.totalQuantity,
        completedQuantity: formData.completedQuantity,
        plannedStart: new Date(formData.plannedStart).toISOString(),
        plannedEnd: new Date(formData.plannedEnd).toISOString(),
        note: formData.note,
        status: formData.status,
      });
      toast.success('制令單已更新', `${editingOrder.id} 更新成功`);
    } else {
      const newId = `MW-${Date.now()}`;
      const newMWO: MainWorkOrder = {
        id: newId,
        orderId: formData.orderId,
        workflowId: formData.workflowId,
        plannedStart: new Date(formData.plannedStart).toISOString(),
        plannedEnd: new Date(formData.plannedEnd).toISOString(),
        totalQuantity: formData.totalQuantity,
        completedQuantity: 0,
        status: '待處理',
        note: formData.note,
        subWorkOrders: [],
      };
      addMainWorkOrder(newMWO);
      // 同步更新父訂單的 mainWorkOrders 列表
      const parentOrder = orders.find((o) => o.id === formData.orderId);
      if (parentOrder) {
        updateOrder(formData.orderId, {
          mainWorkOrders: [...parentOrder.mainWorkOrders, newId],
        });
      }
      toast.success('制令單已建立', `${newId} 建立成功`);
    }
    onClose();
  };

  const inputClass = (field: string) => {
    const base = 'w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium transition-all focus:ring-2 focus:ring-offset-0 focus:outline-none';
    return `${base} ${(errors as any)[field] ? 'border-red-300 focus:ring-red-400' : 'border-slate-200 focus:ring-blue-400 focus:border-blue-300'}`;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditMode ? '編輯制令單' : '新增制令單'} size="lg">
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            所屬訂單 <span className="text-red-500">*</span>
          </label>
          <select
            ref={firstInputRef}
            value={formData.orderId}
            onChange={(e) => handleChange('orderId', e.target.value)}
            onBlur={() => handleBlur('orderId')}
            className={inputClass('orderId')}
            disabled={isEditMode}
          >
            <option value="">請選擇訂單</option>
            {orders.map((o) => (
              <option key={o.id} value={o.id}>
                {o.id} - {o.productName} ({o.customer})
              </option>
            ))}
          </select>
          {errors.orderId && touched.has('orderId') && (
            <p className="mt-1 text-sm text-red-600">{errors.orderId}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            工藝流程 <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.workflowId}
            onChange={(e) => handleChange('workflowId', e.target.value)}
            onBlur={() => handleBlur('workflowId')}
            className={inputClass('workflowId')}
          >
            <option value="">請選擇工藝流程</option>
            {workflows.map((w) => (
              <option key={w.id} value={w.id}>
                {w.id} - {w.name}
              </option>
            ))}
          </select>
          {errors.workflowId && touched.has('workflowId') && (
            <p className="mt-1 text-sm text-red-600">{errors.workflowId}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            數量 <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            min="1"
            value={formData.totalQuantity}
            onChange={(e) => handleChange('totalQuantity', Number(e.target.value))}
            onBlur={() => handleBlur('totalQuantity')}
            className={inputClass('totalQuantity')}
          />
          {errors.totalQuantity && touched.has('totalQuantity') && (
            <p className="mt-1 text-sm text-red-600">{errors.totalQuantity}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              計畫開始日 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.plannedStart}
              onChange={(e) => handleChange('plannedStart', e.target.value)}
              onBlur={() => handleBlur('plannedStart')}
              className={inputClass('plannedStart')}
            />
            {errors.plannedStart && touched.has('plannedStart') && (
              <p className="mt-1 text-sm text-red-600">{errors.plannedStart}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              計畫結束日 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.plannedEnd}
              onChange={(e) => handleChange('plannedEnd', e.target.value)}
              onBlur={() => handleBlur('plannedEnd')}
              className={inputClass('plannedEnd')}
              min={formData.plannedStart}
            />
            {errors.plannedEnd && touched.has('plannedEnd') && (
              <p className="mt-1 text-sm text-red-600">{errors.plannedEnd}</p>
            )}
          </div>
        </div>

        {/* 狀態與已完成數量（僅編輯模式） */}
        {isEditMode && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">狀態</label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none"
              >
                <option value="待處理">待處理</option>
                <option value="進行中">進行中</option>
                <option value="已完成">已完成</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">已完成數量</label>
              <input
                type="number"
                min="0"
                max={formData.totalQuantity}
                value={formData.completedQuantity}
                onChange={(e) => handleChange('completedQuantity', Number(e.target.value))}
                className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none"
              />
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">備註</label>
          <textarea
            value={formData.note}
            onChange={(e) => setFormData((prev) => ({ ...prev, note: e.target.value }))}
            className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none resize-none"
            rows={2}
            placeholder="備註事項（選填）"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold"
          >
            取消
          </button>
          <button
            type="submit"
            className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 active:scale-95"
          >
            {isEditMode ? '儲存變更' : '新增制令單'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
